import moment from "moment";
import { IHttpService, IQService, IPromise, ILogService } from "angular";
import { ClientOptions } from "../../env/client-options";

/**
 * A service that configures the client-side application to communicate with
 * the Apex API.
 */
export class ApiClientService {
    cachedToken: string;
    deferredToken: IPromise<string>

    constructor(private $http: IHttpService, private $log: ILogService,
        private $q: IQService, private apexApiUrl: string,
        private apexClientOptions: ClientOptions) {
        'ngInject';
        this.$http = $http;
        this.$q = $q;
        this.apexApiUrl = apexApiUrl;
    }

    /**
     * Get an access token using the given refresh token. If another token is available,
     * it will be returned instead.
     * @returns The refresh token to be used when there is no usable access token.
     */
    retrieveAccessToken(): IPromise<string> {
        if (this.isValidToken(this.cachedToken)) {
            return this.$q.resolve(this.cachedToken);
        }
        if (this.deferredToken) {
            // Prevent multiple async calls to this method from all triggering the
            // logic for loading the access token.
            return this.deferredToken;
        }
        this.$log.info("Requesting a new auth token");
        const deferredToken = this.$http<string>({
            method: "POST",
            url: `${this.apexApiUrl}/auth/token`,
            data: {
                clientId: this.apexClientOptions.clientId,
                clientSecret: this.apexClientOptions.clientSecret
            },
            disableApexAuthInterceptor: true
        } as any).then(response => this.cachedToken = response.data)
            .finally(() => {
                this.deferredToken = null
                return this.cachedToken;
            });
        this.deferredToken = deferredToken;
        return deferredToken;
    }

    isValidToken(token) {
        if (!token || !token.split) {
            // Ensure token is a non-null string.
            return false;
        }
        const [, payload] = token.split(".");
        try {
            const accessToken: any = JSON.parse(atob(payload));
            if (accessToken.exp && accessToken.exp * 1000 < Date.now()) {
                // Past the expiry date.
                return false;
            }
            if (accessToken.nbf && accessToken.nbf * 1000 > Date.now()) {
                // Before the "not-before" date.
                return false;
            }
            if (accessToken.iat && moment(accessToken.iat * 1000).diff(moment(), "minutes") > 15) {
                // Self-impose an expiry date of 15 minutes.
                return false;
            }
            return true;
        } catch (ex) {
            console.error("Decoding token failed", token, ex);
            return false;
        }

    }
}
